import { useEffect, useRef } from "react";
import { useShoppingCart } from "../context/ShoppingCartContext";
import type { DeliveryFormData } from "../utilities/orderApi";
import {
  CheckoutDeliveryConfig,
  getSyncOrderCheckoutContext,
  isSimpleDeliveryMethod,
} from "./checkoutConfig";

export function useCheckoutFixedDelivery(
  config: CheckoutDeliveryConfig,
  readOnly: boolean,
  suspend: boolean = false,
  includeB2bContactPerson: boolean = false
) {
  const {
    order,
    deliveryMethods,
    syncOrder,
    buyer,
    confirmedOrder,
    cartItems,
    catalogProductsLoaded,
  } = useShoppingCart();
  const checkoutContext = getSyncOrderCheckoutContext(config);
  const fixedUuid = checkoutContext?.deliveryMethodUuid;
  const applyingRef = useRef(false);

  useEffect(() => {
    if (suspend) return;
    if (readOnly) return;
    if (!catalogProductsLoaded) return;
    if (confirmedOrder?.uuid) return;
    if (config.source !== "fixed" || !fixedUuid) return;
    if (!order?.uuid && cartItems.length === 0) return;
    if (order?.delivery?.uuid) return;
    if (applyingRef.current) return;

    const method = deliveryMethods.find((m) => m.uuid === fixedUuid);
    if (!method || !isSimpleDeliveryMethod(method)) return;

    applyingRef.current = true;
    const delivery: DeliveryFormData = {
      methodUuid: method.uuid,
      buyerIsReceiver: true,
      receiverEmail: buyer.email ?? "",
      receiverFirstname: buyer.firstname ?? "",
      receiverSurname: buyer.surname ?? "",
      receiverPhone: buyer.phone ?? "",
    };
    syncOrder({
      delivery,
      checkoutContext,
      includeB2bContactPerson,
    }).finally(() => {
      applyingRef.current = false;
    });
  }, [
    config.source,
    fixedUuid,
    order?.delivery?.uuid,
    deliveryMethods,
    readOnly,
    suspend,
    catalogProductsLoaded,
    confirmedOrder?.uuid,
    order?.uuid,
    cartItems.length,
    buyer.email,
    buyer.firstname,
    buyer.surname,
    buyer.phone,
    syncOrder,
    includeB2bContactPerson,
  ]);
}
